import type { PresenceStatus, PublicUser } from '@itsappy/shared';
import { relativeTime } from '@/lib/utils';

/**
 * How each presence status is described to a person.
 *
 * Kept out of the component barrel so that editing a control does not force a
 * full reload — an object export is not a constant as far as fast refresh is
 * concerned.
 */
export const STATUS_TEXT: Record<PresenceStatus, string> = {
  online: 'Available',
  busy: 'Busy',
  away: 'Away',
  in_meeting: 'In a meeting',
  offline: 'Appear offline',
};

/**
 * How someone *else's* presence reads, as a line of prose.
 *
 * `STATUS_TEXT` describes the status you choose for yourself, so its offline
 * wording ("Appear offline") is wrong here — another person being offline is
 * a last-seen time, not a preference they are announcing.
 */
export function presenceLabel(
  user: Pick<
    PublicUser,
    'status' | 'statusMessage' | 'lastSeenAt' | 'atOffice' | 'officeSiteName' | 'doNotDisturb'
  >,
): string {
  // Physical presence leads: "At the office" is true whether the app is open
  // or not, and it is the part a teammate deciding to drop by cares about.
  // With several saved places, name the one they are actually at.
  const office = user.atOffice ? `At ${user.officeSiteName ?? 'the office'} · ` : '';
  // Then whether they will hear from you: it is the thing that changes what
  // you do next, and it is true whichever status they chose.
  const prefix = user.doNotDisturb ? `${office}Not being disturbed · ` : office;
  if (user.status !== 'offline') return prefix + (user.statusMessage || STATUS_TEXT[user.status]);
  if (!user.lastSeenAt) return prefix + 'Offline';
  // Someone who has this second closed their laptop is "0 seconds ago" to
  // strict distance formatting, which reads as a broken clock rather than a
  // time. Below a minute there is nothing worth counting anyway.
  if (Date.now() - new Date(user.lastSeenAt).getTime() < 60_000) {
    return `${prefix}Last seen just now`;
  }
  return `${prefix}Last seen ${relativeTime(user.lastSeenAt)}`;
}
